// Where the selection sits inside the dock, worked out from the registered name it is on. The cards are named by
// position (`queue-item-3`), so every one of these answers is about a place in the running order rather than about a
// song - and a song that moves takes a different name with it, which is why the callers re-home the selection after
// every move.

const ACTIONS = ['move-earlier', 'move-later', 'remove'] as const;

export type QueueAction = (typeof ACTIONS)[number];

// Which of a focused card's three actions the arrows are on, if any.
export const isActionOf = (focused: string | null) =>
  new RegExp(`^queue-item-\\d+-(${ACTIONS.join('|')})$`).test(focused ?? '');

// The card whose actions are showing: the focused one, or the one an action belongs to. 0 for none.
export const openPosition = (focused: string | null) => Number(focused?.match(/^queue-item-(\d+)/)?.[1] ?? 0);

// Where Enter on a card lands: its first action that its own position does not disable.
export const firstActionOf = (index: number, length: number): QueueAction =>
  index > 0 ? 'move-earlier' : length > 1 ? 'move-later' : 'remove';

// What the selection follows a moved card to. Staying on the same action lets one press of Enter after another walk a
// song along the running order; at either end that action is disabled, so the card itself takes the focus rather than a
// button that is no longer there - which would otherwise leave Enter pointed at the song that had just been displaced,
// undoing the move.
export const landingSpot = (index: number, direction: -1 | 1, length: number) => {
  const position = index + 1 + direction;
  const action = direction === -1 ? 'move-earlier' : 'move-later';
  const atTheEnd = direction === -1 ? position === 1 : position === length;

  return atTheEnd ? `queue-item-${position}` : `queue-item-${position}-${action}`;
};

// What the selection falls back to once a card is removed: the song that takes its place, or the one before it when the
// last card in the order was the one taken away.
export const spotAfterRemoval = (index: number, length: number) => `queue-item-${Math.min(index + 1, length - 1)}`;
